import { convertImageFromDto, HUImageDto, HUImageDtoSchema } from "@modules/dtos/imageDto"
import { ProfileTypeOf } from "@modules/profile/profileTypes"
import {
  Badge,
  FavoritesPersonalRecord,
  Modality,
  Movement,
  PersonalRecord,
  PublicProfile,
  UserBadge,
} from "@modules/profile/publicProfile/publicProfileTypes"
import { convertProgramFromDto, ProgramDto } from "@modules/programs/services/programDto"
import { ScoreType } from "@modules/scoreboard/scoreTypes"
import { convertToEnum } from "@modules/utils/enum"
import { z } from "zod"

export const MovementDtoSchema = z.object({
  id: z.string(),
  movementName: z.string(),
  description: z.string(),
  modality: z.string(),
  scoreType: z.string(),
  videoUrl: z.string().optional(),
})

export const PersonalRecordDtoSchema = z.object({
  id: z.string(),
  userId: z.string(),
  movementId: z.string(),
  value: z.number(),
  repetitionMax: z.number().optional(),
  date: z.string(),
  comment: z.string().optional(),
})

export const BadgeDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  date: z.string().date(),
  obtainable: z.boolean(),
  image: HUImageDtoSchema,
})

export const UserBadgeDtoSchema = z.object({
  userBadgeId: z.string(),
  badge: BadgeDtoSchema,
  additionalInformation: z.string().optional(),
  date: z.string().datetime(),
})

export const FavoritesPersonalRecordsDtoSchema = z.object({
  movement: MovementDtoSchema,
  personalRecord: PersonalRecordDtoSchema,
  favorite: z.boolean(),
})

export type FavoritesPersonalRecordsDto = z.infer<typeof FavoritesPersonalRecordsDtoSchema>
export type UserBadgeDto = z.infer<typeof UserBadgeDtoSchema>
export type MovementDto = z.infer<typeof MovementDtoSchema>
export type PersonalRecordDto = z.infer<typeof PersonalRecordDtoSchema>
export type BadgeDto = z.infer<typeof BadgeDtoSchema>

export type PublicProfileDto = {
  userId: string
  username: string
  firstname?: string
  lastname?: string
  biography?: string
  avatar?: HUImageDto
  birthdate?: string
  crossfitStartDate?: string
  genderCategory?: string
  job?: string
  nationality?: string
  height?: number
  weight?: number
  favoritesPersonalRecords: FavoritesPersonalRecordsDto[]
  ownedPrograms: ProgramDto[]
  followedPrograms: ProgramDto[]
  sharedCoachPrograms: ProgramDto[]
  badges: UserBadgeDto[]
}

export function convertFavoritesPersonalRecordsFromDto(dto: FavoritesPersonalRecordsDto): FavoritesPersonalRecord {
  return {
    ...dto,
    movement: convertMouvementFromDto(dto.movement),
    personalRecord: convertPersonalRecordDtoDto(dto.personalRecord),
  }
}

export function convertMouvementFromDto(dto: MovementDto): Movement {
  return {
    ...dto,
    scoreType: convertToEnum(ScoreType, dto.scoreType, ScoreType.NO_SCORE),
    modality: convertToEnum(Modality, dto.modality, Modality.WEIGHT_LIFTING),
  }
}

export function convertUserBadgeFromDto(dto: UserBadgeDto): UserBadge {
  return {
    ...dto,
    badge: convertBadgeFromDto(dto.badge),
  }
}

export function convertBadgeFromDto(dto: BadgeDto): Badge {
  return {
    ...dto,
    image: convertImageFromDto(dto.image),
    date: new Date(dto.date),
  }
}

export function convertPublicProfileFromDto(dto: PublicProfileDto): PublicProfile {
  return {
    ...dto,
    _type: ProfileTypeOf,
    id: dto.userId,
    birthDate: dto.birthdate ? new Date(dto.birthdate) : undefined,
    avatar: dto.avatar ? convertImageFromDto(dto.avatar) : undefined,
    favoritesPersonalRecords: dto.favoritesPersonalRecords.map(convertFavoritesPersonalRecordsFromDto),
    followedPrograms: dto.followedPrograms.map(convertProgramFromDto),
    ownedPrograms: dto.ownedPrograms.map(convertProgramFromDto),
    sharedCoachPrograms: dto.sharedCoachPrograms.map(convertProgramFromDto),
    badges: dto.badges.map(convertUserBadgeFromDto),
  }
}

export function convertPersonalRecordDtoDto(dto: PersonalRecordDto): PersonalRecord {
  return {
    ...dto,
    date: new Date(dto.date),
  }
}
